import axiosInstance from './axiosInstance';

const API_URL = process.env.REACT_APP_API_URL;

// GET all workOrders
export const getWorkOrders = async () => {
  try {
    const response = await axiosInstance.get(`${API_URL}/work-orders`);
    return response.data;
  } catch (error) {
    console.error("Error fetching workOrders:", error.response?.data || error);
    throw error;
  }
};

// GET a single workOrder
export const getWorkOrderById = async (id) => {
  try {
    const response = await axiosInstance.get(`${API_URL}/work-orders/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching workOrder:", error.response?.data || error);
    throw error;
  }
};

// GET workOrders by event id
export const getWorkOrderByEventId = async (eventId) => {
  try {
    const response = await axiosInstance.get(
      `${API_URL}/work-orders/by-event/${eventId}`,
    );
    return response.data;
  } catch (error) {
    console.error(
      "Error fetching workOrders by eventId:",
      error.response?.data || error,
    );
    throw error;
  }
};

// CREATE a new workOrder
export const createWorkOrder = async (workOrderData) => {
  try {
    const response = await axiosInstance.post(`${API_URL}/work-orders`, workOrderData);
    return response.data;
  } catch (error) {
    console.error("Error creating workOrder:", error.response?.data || error);
    throw error;
  }
};

// CREATE a new workOrder and associated event
export const createWorkOrderAndEvent = async (workOrderData) => {
  try {
    const response = await axiosInstance.post(`${API_URL}/work-orders/with-event`, workOrderData);
    return response.data;
  } catch (error) {
    console.error("Error creating workOrder and event:", error.response?.data || error);
    throw error;
  }
};

// CREATE a new workOrder with AI
export const createWorkOrderWithAi = async (text) => {
  try {
    const response = await axiosInstance.post(`${API_URL}/ai-work-orders`, { text });
    return response.data;
  } catch (error) {
    console.error("Error creating workOrder with AI:", error.response?.data || error);
    throw error;
  }
};

// UPDATE an existing workOrder
export const updateWorkOrder = async (id, workOrderData) => {
  try {
    const response = await axiosInstance.put(`${API_URL}/work-orders/${id}`, workOrderData);
    return response.data;
  } catch (error) {
    console.error("Error updating workOrder:", error.response?.data || error);
    throw error;
  }
};

// DELETE a workOrder
export const deleteWorkOrder = async (id) => {
  try {
    const response = await axiosInstance.delete(`${API_URL}/work-orders/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting workOrder:", error.response?.data || error);
    throw error;
  }
};

// Work Order Search
export const findWorkOrderByNumber = async (workOrderNumber) => {
  const response = await axiosInstance.get(`${API_URL}/work-orders/search`, {
    params: { workOrderNumber }
  });
  return response.data;
};

// Get paginated work orders
export const getPaginatedWorkOrders = async ({
  page = 1,
  pageSize = 10,
  search = '',
  priorityId,
  assetId,
  assetTypeId,
  assetClassId,
  assetGroupId,
  areaId,
  departmentId,
  statusId,
  scheduledAt,
  scheduledAtStart,
  scheduledAtEnd,
  dueDate,
  overdueOnly,
  taskTypeId,
  taskCodeId,
  teamsId,
  crewId,
  contractorsId,
  jobReadyStatus,
  statutory,
  workOrderScheduleId,
  scheduledWithinDays,
  actualWithinDays,
  actualAtStart,
  actualAtEnd,
  linkedTasks,
  linkedEvent,
  dateFilterType,
  dataFilterType,
  dataFromElement,
  sortField,
  sortOrder,
  assignmentFilter,
  requireAssignees,
}) => {
  const response = await axiosInstance.get(`${API_URL}/work-orders`, {
    params: {
      page,
      pageSize,
      search,
      priorityId,
      assetId,
      assetTypeId,
      assetClassId,
      assetGroupId,
      areaId,
      departmentId,
      statusId,
      scheduledAt,
      scheduledAtStart,
      scheduledAtEnd,
      dueDate,
      overdueOnly,
      taskTypeId,
      taskCodeId,
      teamsId,
      crewId,
      contractorsId,
      jobReadyStatus,
      statutory,
      workOrderScheduleId,
      scheduledWithinDays,
      actualWithinDays,
      actualAtStart,
      actualAtEnd,
      linkedTasks,
      linkedEvent,
      dateFilterType,
      dataFilterType,
      dataFromElement,
      sortField,
      sortOrder,
      assignmentFilter,
      requireAssignees,
    },
    paramsSerializer: params => {
      const query = new URLSearchParams();
      for (const key in params) {
        const value = params[key];
        if (Array.isArray(value)) {
          value.forEach(v => query.append(key, v));
        } else if (value !== undefined && value !== null) {
          query.append(key, value);
        }
      }
      return query.toString();
    },
  });
  return response.data;
};

export const getWorkOrderComponentHistory = async ({
  taskCodeIds,
  groupIds,
  taskIds,
  startDate,
  endDate,
  page = 1,
  pageSize = 100,
}) => {
  const response = await axiosInstance.get(
    `${API_URL}/work-orders/component-history`,
    {
      params: {
        taskCodeIds,
        groupIds,
        taskIds,
        startDate,
        endDate,
        page,
        pageSize,
      },
      paramsSerializer: params => {
        const query = new URLSearchParams();
        for (const key in params) {
          const value = params[key];
          if (Array.isArray(value)) {
            value.forEach(v => query.append(key, v));
          } else if (value !== undefined && value !== null) {
            query.append(key, value);
          }
        }
        return query.toString();
      },
    },
  );
  return response.data;
};

// GET work orders in a date range
export const getWorkOrdersInRange = async (start, end) => {
  const response = await axiosInstance.get(`${API_URL}/work-orders/range`, {
    params: { start, end },
  });
  return response.data;
};

// GET work order counts by month
export const getWorkOrderMonthlyStats = async () => {
  const response = await axiosInstance.get(`${API_URL}/work-orders/monthly-stats`);
  return response.data;
};

// GET work order summary stats
export const getWorkOrderSummary = async () => {
  const response = await axiosInstance.get(`${API_URL}/work-orders/summary-stats`);
  return response.data;
};

// GET work order priority summary
export const getWorkOrderPrioritySummary = async () => {
  const response = await axiosInstance.get(`${API_URL}/work-orders/priority-summary`);
  return response.data;
};

// GET work order status summary
export const getWorkOrderStatusSummary = async () => {
  const response = await axiosInstance.get(`${API_URL}/work-orders/status-summary`);
  return response.data;
};

// GET work order breakdown summary
export const getWorkOrderBreakdownSummary = async () => {
  const response = await axiosInstance.get(`${API_URL}/work-orders/breakdown-summary`);
  return response.data;
};

// GET work order asset class summary
export const getWorkOrderAssetClassSummary = async () => {
  const response = await axiosInstance.get(`${API_URL}/work-orders/asset-class-summary`);
  return response.data;
};

// GET dashboard work order counts
export const getWorkOrderDashboardCounts = async ({ teamIds = [], userId } = {}) => {
  const response = await axiosInstance.get(`${API_URL}/work-orders/dashboard-stats`, {
    params: {
      teamIds,
      userId,
    },
    paramsSerializer: (params) => {
      const query = new URLSearchParams();
      for (const key in params) {
        const value = params[key];
        if (Array.isArray(value)) {
          value.forEach((v) => query.append(key, v));
        } else if (value !== undefined && value !== null) {
          query.append(key, value);
        }
      }
      return query.toString();
    },
  });
  return response.data;
};

export const getMaintenanceIntensity = async ({ assetIds = [], startDate, endDate } = {}) => {
  const response = await axiosInstance.post(`${API_URL}/work-orders/maintenance-intensity`, {
    assetIds,
    startDate,
    endDate,
  });
  return response.data;
};

export const confirmWorkOrderMaterials = async (id) => {
  const response = await axiosInstance.post(
    `${API_URL}/work-orders/${id}/confirm-materials`,
    {},
    {},
  );
  return response.data;
};

// GET top failure codes for completed work orders
export const getTopFailureCodes = async () => {
  const response = await axiosInstance.get(`${API_URL}/work-orders/failure-summary`);
  return response.data;
};

// GET top cause codes for completed work orders
export const getTopCauseCodes = async () => {
  const response = await axiosInstance.get(`${API_URL}/work-orders/cause-summary`);
  return response.data;
};

// GET outstanding work orders by department
export const getOutstandingByDepartment = async () => {
  const response = await axiosInstance.get(`${API_URL}/work-orders/department-outstanding-summary`);
  return response.data;
};


export const getWorkOrderCountSummary = async ({ search = '' } = {}) => {
  const response = await axiosInstance.get(`${API_URL}/work-orders/count`, {
    params: { search },
  });
  return response.data;
};
export const getWorkOrdersByTaskId = async (taskId) => {
  const response = await axiosInstance.get(`${API_URL}/work-orders/by-task/${taskId}`);
  return response.data;
};


export const exportWorkOrderPdf = async (id) => {
  const response = await axiosInstance.get(`${API_URL}/work-orders/${id}/pdf`, {
    responseType: 'blob',
  });
  return response.data;
};
